import type { RacquetSportId } from './racquetSports';
import type {
  ProgramTemplateDefinition,
  ProgramTemplateProviderId,
  ProgramTemplateStageDefinition,
  ProgramTemplateStageSkillLevel,
} from '../types/programTemplate';

function skills(names: string[]): { name: string }[] {
  return names.map((name) => ({ name }));
}

function stage(
  stageKey: string,
  name: string,
  description: string,
  skillLevel: ProgramTemplateStageSkillLevel,
  recommendedAgeRangeLabel: string,
  previousStageKey: string | null,
  goals: string[],
  targetSkillNames: string[],
  sources?: string
): ProgramTemplateStageDefinition {
  return {
    stageKey,
    name,
    description,
    skillLevel,
    recommendedAgeRangeLabel,
    previousStageKey,
    goals: goals.map((g) => `• ${g}`).join('\n'),
    targetSkills: skills(targetSkillNames),
    sources,
  };
}

const TENNIS_AUSTRALIA_STAGES: ProgramTemplateStageDefinition[] = [
  stage(
    'blue',
    'Blue Stage (Launch Pad / Introduce)',
    'The entry-level stage focused on developing fundamental movement and coordination skills through fun, game-based activities. Children learn basic ball and racquet awareness while building confidence and enjoyment of sport.',
    'beginner',
    '3–5+ years',
    null,
    [
      'Develop fundamental motor skills.',
      'Build confidence and enjoyment.',
      'Introduce basic tennis concepts.',
      'Improve coordination and movement patterns.',
    ],
    [
      'Running',
      'Jumping',
      'Hopping',
      'Balancing',
      'Throwing',
      'Catching',
      'Spatial awareness',
      'Hand-eye coordination',
      'Racquet familiarity',
      'Social interaction',
      'Following instructions',
      'Basic ball tracking',
    ]
  ),
  stage(
    'red',
    'Red Stage (Explore)',
    'The first tennis-specific development stage. Players use low-compression red balls, smaller courts, and smaller racquets to learn basic strokes and rallying skills in an environment suited to their size and ability.',
    'beginner',
    '5–8 years',
    'blue',
    [
      'Learn basic tennis techniques.',
      'Develop rallying ability.',
      'Improve hand-eye coordination.',
      'Introduce serving and game play.',
      'Build confidence through success.',
    ],
    [
      'Forehand fundamentals',
      'Backhand fundamentals',
      'Basic serve',
      'Rallying',
      'Ball control',
      'Court movement',
      'Hand-eye coordination',
      'Recovery position',
      'Basic scoring awareness',
      'Sportsmanship',
      'Teamwork',
      'Persistence',
    ]
  ),
  stage(
    'orange',
    'Orange Stage (Develop)',
    'Players transition to a larger court and a faster orange ball. The focus shifts from basic stroke production toward consistency, tactics, and introductory match play.',
    'beginner',
    '8–10 years',
    'red',
    [
      'Improve stroke consistency.',
      'Develop tactical awareness.',
      'Learn match-play concepts.',
      'Increase rally length and control.',
      'Strengthen confidence and concentration.',
    ],
    [
      'Consistent forehand and backhand',
      'Improved serve',
      'Return of serve',
      'Directional control',
      'Court positioning',
      'Basic tactics',
      'Point construction',
      'Match play fundamentals',
      'Concentration',
      'Coordination',
      'Group interaction',
      'Decision-making',
    ]
  ),
  stage(
    'green',
    'Green Stage (Encourage)',
    'Players move onto a full-size court using green balls with higher compression. The stage bridges junior development and full yellow-ball tennis by emphasizing advanced technique, tactics, and competitive play.',
    'intermediate',
    '9–12+ years',
    'orange',
    [
      'Refine technique.',
      'Develop tactical understanding.',
      'Prepare for full-court tennis.',
      'Improve match-play performance.',
      'Build independence and resilience.',
    ],
    [
      'Advanced forehand and backhand technique',
      'Serve development',
      'Return strategies',
      'Volleys',
      'Overheads',
      'Spin production',
      'Tactical awareness',
      'Match strategy',
      'Competitive play',
      'Leadership',
      'Resilience',
      'Problem-solving during points',
    ]
  ),
  stage(
    'yellow',
    'Yellow Ball Stage (Transition to Competition)',
    'The final progression stage where players use standard tennis balls and compete on full-size courts. Players continue developing technical, tactical, physical, and competitive skills.',
    'intermediate',
    'Typically 12+ years (or when ready)',
    'green',
    [
      'Transition to standard tennis conditions.',
      'Prepare for tournaments and competition.',
      'Refine advanced techniques and tactics.',
      'Develop long-term participation or performance pathways.',
    ],
    [
      'Full-court match play',
      'Advanced serving',
      'Tactical planning',
      'Competitive mindset',
      'Shot selection',
      'Point construction',
      'Physical conditioning',
      'Mental resilience',
      'Tournament preparation',
      'Strategic adaptation',
      'Leadership and sportsmanship',
    ]
  ),
];

const USTA_STAGES: ProgramTemplateStageDefinition[] = [
  stage(
    'red',
    'Red Ball Stage',
    'The entry stage of the U.S. Tennis Association (USTA) youth development pathway. Players use oversized low-compression red balls, smaller racquets, and reduced-size courts to develop fundamental movement, coordination, and basic tennis skills. The emphasis is on fun, success, and learning through play.',
    'beginner',
    '4–8 years (sometimes up to 9 depending on development)',
    null,
    [
      'Develop athletic movement skills.',
      'Introduce racquet and ball control.',
      'Build confidence through successful rallies.',
      'Create enjoyment and engagement with tennis.',
      'Establish fundamental stroke patterns.',
    ],
    [
      'Hand-eye coordination',
      'Throwing and catching',
      'Balance and agility',
      'Forehand fundamentals',
      'Backhand fundamentals',
      'Racquet control',
      'Ball tracking',
      'Basic rallying',
      'Court awareness',
      'Movement and recovery',
      'Listening and following instructions',
      'Sportsmanship',
    ]
  ),
  stage(
    'orange',
    'Orange Ball Stage',
    'Players transition to a larger court and a faster orange ball. This stage focuses on improving consistency, introducing tactical thinking, and developing the ability to serve, rally, and score. The orange ball still moves slower than a standard ball, allowing players to learn proper technique and decision-making.',
    'beginner',
    '7–10 years (recommended)',
    'red',
    [
      'Improve stroke consistency.',
      'Introduce tactical awareness.',
      'Learn serving and scoring.',
      'Develop longer rallies.',
      'Increase confidence during match play.',
    ],
    [
      'Consistent forehand and backhand',
      'Serve fundamentals',
      'Return of serve',
      'Rally construction',
      'Directional control',
      'Court positioning',
      'Basic tactics',
      'Footwork patterns',
      'Match scoring',
      'Decision-making',
      'Competitive play habits',
      'Concentration',
    ]
  ),
  stage(
    'green',
    'Green Ball Stage',
    'The final developmental stage before standard tennis. Players compete on full-size courts using green-dot balls with slightly reduced compression. The focus shifts toward advanced technique, tactical understanding, and competitive readiness while maintaining manageable ball speed and bounce.',
    'intermediate',
    '9–12 years (recommended)',
    'orange',
    [
      'Prepare players for full-court tennis.',
      'Improve tactical awareness.',
      'Develop advanced stroke mechanics.',
      'Increase match-play experience.',
      'Build confidence in competitive environments.',
    ],
    [
      'Advanced forehand and backhand technique',
      'Serve development',
      'Return strategies',
      'Volley fundamentals',
      'Overhead shots',
      'Court coverage',
      'Tactical problem-solving',
      'Point construction',
      'Match management',
      'Competitive resilience',
      'Strategic decision-making',
      'Sportsmanship and self-management',
    ]
  ),
  stage(
    'yellow',
    'Yellow Ball Stage',
    'The standard form of tennis played on a full-size court using regular yellow tennis balls. Players continue refining technical, tactical, physical, and mental skills while participating in more structured competition and tournament pathways.',
    'intermediate',
    '11–12+ years (or when progression requirements are met)',
    'green',
    [
      'Transition to full competitive tennis.',
      'Refine advanced technical skills.',
      'Develop high-level tactical understanding.',
      'Build tournament experience.',
      'Support long-term player development.',
    ],
    [
      'Advanced serving techniques',
      'Aggressive and defensive shot selection',
      'Tactical planning',
      'Competitive match play',
      'Mental toughness',
      'Physical conditioning',
      'Strategic adaptation',
      'Net play',
      'Spin variation',
      'Match analysis',
      'Tournament preparation',
      'Leadership and sportsmanship',
    ]
  ),
];

const EUROPE_TENNIS10S_STAGES: ProgramTemplateStageDefinition[] = [
  stage(
    'red',
    'Red Stage (Tennis10s Red)',
    'The introductory stage designed for young players using low-compression red balls, smaller courts, and shorter racquets. The emphasis is on movement, coordination, basic rallying, and enjoyment of tennis through games and activities.',
    'beginner',
    '5–8 years',
    null,
    [
      'Develop physical literacy.',
      'Introduce basic tennis skills.',
      'Encourage successful rallying.',
      'Build confidence and enjoyment.',
      'Establish foundational movement patterns.',
    ],
    [
      'Hand-eye coordination',
      'Throwing and catching',
      'Balance and agility',
      'Forehand introduction',
      'Backhand introduction',
      'Racquet control',
      'Ball tracking',
      'Basic rallying',
      'Court awareness',
      'Movement and recovery',
      'Social interaction',
      'Sportsmanship',
    ],
    'ITF Tennis10s model adopted across Europe.'
  ),
  stage(
    'orange',
    'Orange Stage (Tennis10s Orange)',
    'Players move onto a larger court using orange balls that bounce higher and travel faster than red balls. Tactical awareness begins to develop alongside technical consistency.',
    'beginner',
    '8–10 years',
    'red',
    [
      'Improve rally consistency.',
      'Introduce tactical thinking.',
      'Develop serving and returning.',
      'Increase match-play experience.',
      'Improve court positioning.',
    ],
    [
      'Consistent forehand and backhand',
      'Basic serve',
      'Return of serve',
      'Rally construction',
      'Directional control',
      'Court positioning',
      'Footwork patterns',
      'Tactical awareness',
      'Scoring knowledge',
      'Decision-making',
      'Concentration',
      'Sportsmanship',
    ],
    'ITF Tennis10s progression framework.'
  ),
  stage(
    'green',
    'Green Stage (Tennis10s Green)',
    'Players transition to full-size courts using green-dot balls with reduced compression. The stage bridges junior tennis and standard competition while refining technique and tactical understanding.',
    'intermediate',
    '9–10 years',
    'orange',
    [
      'Prepare players for standard tennis.',
      'Improve tactical decision-making.',
      'Refine stroke technique.',
      'Increase match-play competence.',
      'Build competitive confidence.',
    ],
    [
      'Advanced forehand and backhand technique',
      'Serve development',
      'Return strategies',
      'Volley fundamentals',
      'Overheads',
      'Court coverage',
      'Tactical problem-solving',
      'Point construction',
      'Match management',
      'Competitive resilience',
      'Strategic awareness',
      'Self-management',
    ],
    'ITF Tennis10s and Tennis Europe development guidelines.'
  ),
  stage(
    'yellow',
    'Yellow Ball Stage',
    'Players compete using standard yellow balls on full-size courts. The focus shifts toward performance development, tournament play, advanced tactics, and long-term competitive growth.',
    'intermediate',
    '10+ years',
    'green',
    [
      'Transition to standard competition.',
      'Refine advanced technical skills.',
      'Develop strategic match play.',
      'Build physical and mental resilience.',
      'Support long-term player development.',
    ],
    [
      'Advanced serving',
      'Tactical planning',
      'Spin variation',
      'Net play',
      'Competitive match play',
      'Physical conditioning',
      'Mental toughness',
      'Match analysis',
      'Tournament preparation',
      'Strategic adaptation',
      'Leadership',
      'Sportsmanship',
    ],
    'ITF Tennis10s and Tennis Europe competition structure.'
  ),
];

const UK_LTA_YOUTH_STAGES: ProgramTemplateStageDefinition[] = [
  stage(
    'blue',
    'Blue Stage',
    'An introductory movement-based stage focused on balance, agility, coordination, and basic tennis awareness. Activities are highly game-oriented, with minimal emphasis on competition.',
    'beginner',
    '4–6 years',
    null,
    [
      'Develop fundamental movement skills.',
      'Build confidence and enjoyment.',
      'Introduce tennis concepts.',
      'Improve coordination and athletic development.',
    ],
    [
      'Running',
      'Jumping',
      'Balancing',
      'Agility',
      'Coordination',
      'Throwing',
      'Catching',
      'Spatial awareness',
      'Ball tracking',
      'Basic racquet familiarity',
      'Social interaction',
      'Following instructions',
    ],
    'LTA Youth Blue Stage.'
  ),
  stage(
    'red',
    'Red Stage',
    'Children begin learning overarm serves, rallying, scoring, and basic competition using softer balls and smaller courts designed for their size and ability.',
    'beginner',
    '6–8 years',
    'blue',
    [
      'Learn to serve, rally, and score.',
      'Develop coordination and movement.',
      'Build confidence through success.',
      'Introduce competition.',
    ],
    [
      'Forehand fundamentals',
      'Backhand fundamentals',
      'Basic serve',
      'Rallying',
      'Ball control',
      'Court movement',
      'Recovery position',
      'Basic scoring',
      'Hand-eye coordination',
      'Speed and agility',
      'Teamwork',
      'Sportsmanship',
    ],
    'LTA Youth Red Stage.'
  ),
  stage(
    'orange',
    'Orange Stage',
    'Players develop a more complete game while learning tactics, problem-solving, sportsmanship, and leadership. Court size and ball speed increase to support progression.',
    'beginner',
    '8–9 years',
    'red',
    [
      'Develop a rounded game.',
      'Improve consistency.',
      'Introduce tactical thinking.',
      'Strengthen competitive awareness.',
    ],
    [
      'Consistent forehand and backhand',
      'Serve and return',
      'Tactical awareness',
      'Court positioning',
      'Rally construction',
      'Problem-solving',
      'Decision-making',
      'Match-play fundamentals',
      'Leadership',
      'Sportsmanship',
      'Communication',
      'Concentration',
    ],
    'LTA Youth Orange Stage.'
  ),
  stage(
    'green',
    'Green Stage',
    'Players move to full-size courts and begin refining technical and tactical skills while developing greater independence during training and competition.',
    'intermediate',
    '9–10 years',
    'orange',
    [
      'Refine technique.',
      'Improve tactical understanding.',
      'Increase independence.',
      'Prepare for yellow-ball competition.',
    ],
    [
      'Advanced stroke technique',
      'Serve development',
      'Return strategies',
      'Court coverage',
      'Tactical awareness',
      'Point construction',
      'Match management',
      'Independent decision-making',
      'Competitive resilience',
      'Leadership',
      'Problem-solving',
      'Self-assessment',
    ],
    'LTA Youth Green Stage.'
  ),
  stage(
    'yellow',
    'Yellow Stage',
    'Players compete on full-size courts with standard yellow balls and begin developing their own playing style while continuing technical and tactical growth.',
    'intermediate',
    '10+ years',
    'green',
    [
      'Transition into full tennis.',
      'Develop personal playing style.',
      'Improve advanced tactics.',
      'Prepare for competitive pathways.',
    ],
    [
      'Advanced serving',
      'Tactical planning',
      'Match strategy',
      'Spin development',
      'Competitive match play',
      'Mental resilience',
      'Physical conditioning',
      'Strategic adaptation',
      'Tournament preparation',
      'Leadership',
      'Self-management',
      'Sportsmanship',
    ],
    'LTA Youth pathway.'
  ),
];

const PICKLEBALL_RATING_STAGES: ProgramTemplateStageDefinition[] = [
  stage(
    'beginner',
    'Beginner (2.0)',
    'Players learn the rules, court positioning, and basic strokes. Focus on getting the ball in play, understanding the non-volley zone, and building confidence through cooperative rallies.',
    'beginner',
    'All ages — new players',
    null,
    [
      'Learn basic rules and scoring.',
      'Understand court zones and positioning.',
      'Develop consistent serve and return.',
      'Build confidence through cooperative play.',
    ],
    [
      'Serve and return basics',
      'Non-volley zone rules',
      'Forehand and backhand groundstrokes',
      'Court positioning',
      'Basic scoring',
      'Hand-eye coordination',
      'Footwork fundamentals',
      'Partner communication',
    ]
  ),
  stage(
    'social',
    'Social Player (2.5–3.0)',
    'Players develop consistent rallies, understand kitchen play, and begin using the third-shot drop. Focus on placement over power and basic doubles strategy.',
    'beginner',
    'All ages — recreational players',
    'beginner',
    [
      'Extend rally length with consistency.',
      'Master kitchen and dinking fundamentals.',
      'Introduce third-shot drop strategy.',
      'Develop basic doubles positioning.',
    ],
    [
      'Dinking',
      'Third-shot drop',
      'Kitchen play',
      'Volley control',
      'Serve depth and placement',
      'Return depth',
      'Doubles positioning',
      'Shot selection',
      'Reset from defense',
    ]
  ),
  stage(
    'intermediate',
    'Intermediate (3.5)',
    'Players refine shot variety, improve transition play, and develop tactical awareness. Focus on attacking opportunities, defensive resets, and partner stacking.',
    'intermediate',
    'All ages — developing competitors',
    'social',
    [
      'Improve shot variety and consistency.',
      'Develop transition and attack patterns.',
      'Master stacking and partner movement.',
      'Build tactical decision-making.',
    ],
    [
      'Transition play',
      'Attacking volleys',
      'Defensive resets',
      'Stacking and partner movement',
      'Lob and overhead',
      'Speed-up opportunities',
      'Cross-court dinking',
      'Point construction',
      'Pressure handling',
    ]
  ),
  stage(
    'advanced',
    'Advanced / Competitive (4.0+)',
    'Players compete at a high level with refined technique, advanced tactics, and mental toughness. Focus on tournament preparation, pattern play, and elite doubles strategy.',
    'advanced',
    'All ages — competitive players',
    'intermediate',
    [
      'Compete with refined technique and tactics.',
      'Execute advanced patterns under pressure.',
      'Prepare for tournament play.',
      'Lead and communicate effectively as a partner.',
    ],
    [
      'Advanced shot selection',
      'Pattern play',
      'Erne and ATP opportunities',
      'Counter-attack',
      'Mental toughness',
      'Tournament preparation',
      'Partner leadership',
      'Physical conditioning',
      'Strategic adaptation',
    ]
  ),
];

const PADEL_CATEGORY_STAGES: ProgramTemplateStageDefinition[] = [
  stage(
    'beginner',
    'Beginner (Introduction)',
    'Players learn padel fundamentals: court dimensions, glass walls, basic strokes, and partner positioning. Focus on getting rallies started and understanding the unique rules of the game.',
    'beginner',
    'All ages — new players',
    null,
    [
      'Learn padel rules and court layout.',
      'Understand wall play basics.',
      'Develop forehand and backhand strokes.',
      'Build confidence through cooperative rallies.',
    ],
    [
      'Serve and return basics',
      'Forehand and backhand groundstrokes',
      'Court positioning',
      'Basic wall awareness',
      'Partner communication',
      'Footwork fundamentals',
      'Basic scoring',
      'Hand-eye coordination',
    ]
  ),
  stage(
    'improver',
    'Improver',
    'Players develop consistent rallies, learn to use the back and side glass, and improve net approach. Focus on controlled volleys and basic pair movement.',
    'beginner',
    'All ages — developing players',
    'beginner',
    [
      'Use back and side glass effectively.',
      'Improve volley consistency at the net.',
      'Develop basic pair movement.',
      'Extend rally length with control.',
    ],
    [
      'Back glass returns',
      'Side glass awareness',
      'Volley control',
      'Net approach',
      'Lob basics',
      'Pair movement',
      'Defensive positioning',
      'Shot placement',
    ]
  ),
  stage(
    'intermediate',
    'Intermediate',
    'Players refine wall play, introduce bandeja and vibora, and develop tactical point construction. Focus on net dominance and effective pair communication.',
    'intermediate',
    'All ages — club-level players',
    'improver',
    [
      'Master bandeja and defensive lobs.',
      'Develop vibora and attacking opportunities.',
      'Improve point construction and tactics.',
      'Strengthen pair communication and movement.',
    ],
    [
      'Bandeja',
      'Vibora',
      'Wall play (back and side)',
      'Lob and defensive recovery',
      'Net positioning',
      'Pair movement and communication',
      'Point construction',
      'Transition play',
    ]
  ),
  stage(
    'advanced',
    'Advanced / Competitive',
    'Players compete at a high level with refined technique, advanced wall play, and elite doubles strategy. Focus on tournament preparation and mental resilience.',
    'advanced',
    'All ages — competitive players',
    'intermediate',
    [
      'Compete with elite technique and tactics.',
      'Execute advanced wall and net patterns.',
      'Prepare for tournament play.',
      'Lead and adapt as a pair under pressure.',
    ],
    [
      'Advanced wall play',
      'Bandeja and vibora mastery',
      'Counter-attack',
      'Mental toughness',
      'Tournament preparation',
      'Pair leadership',
      'Strategic adaptation',
      'Physical conditioning',
      'Point patterns under pressure',
    ]
  ),
];

export const PROGRAM_TEMPLATE_DEFINITIONS: ProgramTemplateDefinition[] = [
  {
    templateId: 'tennis_australia',
    sportId: 'tennis',
    templateName: 'Australian-style pathway',
    shortDescription: 'Launch Pad through Yellow Ball — common Australian junior ball-color progression.',
    regionLabel: 'Australia',
    stages: TENNIS_AUSTRALIA_STAGES,
  },
  {
    templateId: 'usta',
    sportId: 'tennis',
    templateName: 'US-style pathway',
    shortDescription:
      'Red through Yellow Ball — common U.S. youth ball-color progression (Net Generation–style readiness).',
    regionLabel: 'United States',
    stages: USTA_STAGES,
    frameworkNote:
      'Many U.S. programs also track Net Generation / Youth Progression readiness across Red, Orange, Green, and Yellow (American Development Model). That framework runs alongside ball-color stages and is not imported as a separate program.',
  },
  {
    templateId: 'europe_tennis10s',
    sportId: 'tennis',
    templateName: 'European Tennis10s-style',
    shortDescription: 'Tennis10s Red through Yellow — common European junior ball-color progression.',
    regionLabel: 'Europe',
    stages: EUROPE_TENNIS10S_STAGES,
  },
  {
    templateId: 'uk_lta_youth',
    sportId: 'tennis',
    templateName: 'UK youth-style pathway',
    shortDescription: 'Blue through Yellow — common UK junior ball-color progression.',
    regionLabel: 'United Kingdom',
    stages: UK_LTA_YOUTH_STAGES,
  },
  {
    templateId: 'pickleball_rating',
    sportId: 'pickleball',
    templateName: 'Pickleball rating-style pathway',
    shortDescription: 'Beginner (2.0) through Advanced (4.0+) — common recreational rating progression.',
    regionLabel: 'All regions',
    stages: PICKLEBALL_RATING_STAGES,
  },
  {
    templateId: 'padel_category',
    sportId: 'padel',
    templateName: 'Padel category-style pathway',
    shortDescription: 'Beginner through Advanced — common club-level category progression.',
    regionLabel: 'All regions',
    stages: PADEL_CATEGORY_STAGES,
  },
];

export function getProgramTemplateById(
  templateId: ProgramTemplateProviderId
): ProgramTemplateDefinition | undefined {
  return PROGRAM_TEMPLATE_DEFINITIONS.find((t) => t.templateId === templateId);
}

export function listProgramTemplateSummaries(): Pick<
  ProgramTemplateDefinition,
  'templateId' | 'sportId' | 'templateName' | 'shortDescription' | 'regionLabel' | 'frameworkNote'
>[] {
  return PROGRAM_TEMPLATE_DEFINITIONS.map(
    ({ templateId, sportId, templateName, shortDescription, regionLabel, frameworkNote }) => ({
      templateId,
      sportId,
      templateName,
      shortDescription,
      regionLabel,
      frameworkNote,
    })
  );
}

/** Returns templates compatible with the given platform sport id. */
export function listProgramTemplatesForSport(sportId: RacquetSportId): ProgramTemplateDefinition[] {
  return PROGRAM_TEMPLATE_DEFINITIONS.filter((t) => t.sportId === sportId);
}

/** Returns templates compatible with any of the given platform sport ids. */
export function listProgramTemplatesForSports(
  sportIds: readonly RacquetSportId[]
): ProgramTemplateDefinition[] {
  const idSet = new Set(sportIds);
  return PROGRAM_TEMPLATE_DEFINITIONS.filter((t) => idSet.has(t.sportId));
}
